import recetaPlatoService from '../services/recetaPlatoService';
import entryGuideService from '../services/entryGuideService';
import produccionGuiaService from '../services/produccionGuiaService';
import { productService } from '../services/productService';
import { getGuiaConfigByGuideType } from './produccionGuiaPaths';
import {
  applyCoproductosToInsumoResumen,
  applyKardexToInsumoResumen,
  buildExplosionPepAnalisis,
  buildResumenByproductsFromInsumos,
  buildResumenInsumosGuia,
  buildResumenPlatosGuia,
  getGuiaItemsWithQuantity,
  isSubproductoProduct,
} from './produccionGuiaCostos';

function normalizarHoraHHmm(raw, fallback = '08:00') {
  const t = String(raw || '').trim();
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return fallback;
  const hh = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  const mm = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function mapInsumoRow(row, origen = 'PT') {
  const despacho = row.cantidadDespacho != null ? Number(row.cantidadDespacho) : Number(row.quantity || 0);
  return {
    origen,
    product: row.product || '—',
    unit: row.unit || '—',
    quantity: Number(row.quantity || 0),
    cantidadDespacho: despacho,
    costoUnitario: Number(row.costoUnitarioDespacho ?? row.costoUnitarioKardex ?? 0),
    subtotal: Number(row.subtotalDespacho ?? row.subtotalKardex ?? 0),
    esSubproductoCosteado: Boolean(row.esSubproductoCosteado),
  };
}

function mapElaboradoRow(row) {
  const unit =
    row.unit_of_measure ||
    row.output_unit_of_measure ||
    'unidad';
  return {
    receta_name: row.receta_name || '—',
    receta_code: row.receta_code || '',
    classification: row.classification || '—',
    quantity: Number(row.quantity || 0),
    unit,
    unitPrice: Number(row.unitPrice || 0),
    totalCost: Number(row.totalCost || 0),
  };
}

export function buildProduccionGuiaInforme({
  guia,
  itemsWithQty,
  recetasFullById,
  stockCostByProduct,
  productUnitById,
  productTipoById,
  pepRecetaIdByProductId,
  pepRecetasByProductId,
  produccionFabricarPepDesdePtEnabled,
  horaCorte,
}) {
  const config = getGuiaConfigByGuideType(guia.guide_type);
  const esProductoTerminado = guia.guide_type === 'producto_terminado';

  const resumenInsumos = buildResumenInsumosGuia(itemsWithQty, recetasFullById);

  const explosionPep = esProductoTerminado
    ? buildExplosionPepAnalisis({
        itemsWithQty,
        recetasFullById,
        pepRecetasByProductId,
        stockCostByProduct,
        productUnitById,
        productTipoById,
        globalFabricarSubrecetaDefault: produccionFabricarPepDesdePtEnabled,
      })
    : {
        hasPep: false,
        insumosPep: { filas: [], totalCosto: 0 },
        insumosAgregados: { filas: [], totalCosto: 0 },
        resumenSubproductos: { rows: [], costTotal: 0 },
        resumenCoproductosPep: { rows: [], costTotal: 0 },
        pepUnitCostByProductId: {},
      };

  const resumenInsumosKardexBase = applyKardexToInsumoResumen(
    resumenInsumos,
    stockCostByProduct,
    productUnitById,
  );

  const resumenInsumosKardex = explosionPep.hasPep ? explosionPep.insumosAgregados : resumenInsumosKardexBase;
  const resumenInsumosPepKardex = explosionPep.hasPep ? explosionPep.insumosPep : { filas: [], totalCosto: 0 };

  const consumoPepByProductId = new Map();
  for (const row of resumenInsumosPepKardex.filas || []) {
    if (row?.product_id == null) continue;
    const pid = String(row.product_id);
    consumoPepByProductId.set(pid, (consumoPepByProductId.get(pid) || 0) + Number(row.quantity || 0));
  }

  let resumenInsumosKardexPt = {
    ...resumenInsumosKardex,
    filas: (resumenInsumosKardex.filas || []).map((row) => {
      if (row.esSubproductoCosteado || row.product_id == null) return row;
      const consumoPep = Number(consumoPepByProductId.get(String(row.product_id)) || 0);
      if (!(consumoPep > 0)) return row;
      const stockAntes = row.stockKardex == null ? null : Number(row.stockKardex);
      const stockDisponible =
        stockAntes == null || !Number.isFinite(stockAntes) ? null : stockAntes - consumoPep;
      return {
        ...row,
        stockAntesPep: stockAntes,
        consumoPep,
        stockKardex: stockDisponible,
        stockAjustadoPorPep: true,
      };
    }),
  };

  if (explosionPep.hasPep && (explosionPep.resumenCoproductosPep?.rows?.length > 0)) {
    resumenInsumosKardexPt = applyCoproductosToInsumoResumen(
      resumenInsumosKardexPt,
      explosionPep.resumenCoproductosPep,
    );
  }

  const resumenPlatos = buildResumenPlatosGuia(
    itemsWithQty,
    recetasFullById,
    stockCostByProduct,
    explosionPep.hasPep ? explosionPep.pepUnitCostByProductId : {},
  );

  const resumenByproducts = buildResumenByproductsFromInsumos(
    itemsWithQty,
    recetasFullById,
    productUnitById,
  );

  const insumosPep = (resumenInsumosPepKardex.filas || [])
    .filter((r) => !r.esSubproductoCosteado)
    .map((r) => mapInsumoRow(r, 'PEP'));
  const insumosPt = (resumenInsumosKardexPt.filas || [])
    .filter((r) => !r.esSubproductoCosteado)
    .map((r) => mapInsumoRow(r, 'PT'));

  const costoInsumosTotal =
    (explosionPep.hasPep ? Number(resumenInsumosPepKardex.totalCosto || 0) : 0) +
    Number(resumenInsumosKardexPt.totalCosto || 0);

  const elaborados = (resumenPlatos.rows || [])
    .filter((r) => Number(r.quantity) > 0)
    .map(mapElaboradoRow);

  const costoElaboradosTotal = elaborados.reduce((s, r) => s + r.totalCost, 0);

  const guiasVinculadas = [];
  const pushGuia = (tipo, code, id) => {
    if (!code) return;
    guiasVinculadas.push({ tipo, code, id: id || null });
  };
  pushGuia('PEP salida', guia.pep_salida_guide_code, guia.pep_salida_guide_id);
  pushGuia('PEP entrada', guia.pep_entrada_guide_code, guia.pep_entrada_guide_id);
  pushGuia('Salida insumos', guia.salida_guide_code, guia.salida_guide_id);
  pushGuia('Entrada elaborados', guia.entrada_guide_code, guia.entrada_guide_id);
  if (guia.tratado_como_venta) {
    pushGuia('Salida venta', guia.venta_guide_code, guia.venta_guide_id);
  }
  for (const d of guia.distribucion_guides || []) {
    if (d.traspaso_guide_code) {
      guiasVinculadas.push({
        tipo: `Traspaso → ${d.destination_warehouse_name || 'destino'}`,
        code: d.traspaso_guide_code,
        id: d.traspaso_guide_id || d.traspaso_id || null,
        guideKind: 'traspaso',
      });
    }
    if (d.recepcion_guide_code) {
      guiasVinculadas.push({
        tipo: `Recepción ← ${d.destination_warehouse_name || 'destino'}`,
        code: d.recepcion_guide_code,
        id: d.recepcion_guide_id || d.recepcion_id || null,
        guideKind: 'recepcion',
      });
    }
  }

  return {
    meta: {
      generatedAt: new Date().toISOString(),
      tipoTitulo: config?.title || 'Guía de producción',
      kardexCorte: `${guia.guide_date || ''} ${horaCorte}`.trim(),
    },
    guia: {
      id: guia.id,
      code: guia.code,
      guide_type: guia.guide_type,
      guide_date: guia.guide_date,
      guide_time: guia.guide_time,
      warehouse_name: guia.warehouse_name,
      project_name: guia.project_name,
      project_type: guia.project_type,
      produccion_linea_name: guia.produccion_linea_name,
      production_status: guia.production_status,
      production_status_label:
        guia.production_status === 'en_produccion' ? 'En producción' : 'Registrada',
      notes: guia.notes || '',
      recetas_count: itemsWithQty.length,
    },
    guiasVinculadas,
    elaborados,
    insumosPep,
    insumosPt,
    subproductosPep: (explosionPep.resumenSubproductos?.rows || []).map((r) => ({
      product: r.product_name || r.product || '—',
      unit: r.unit_of_measure || r.unit || '—',
      quantity: Number(r.quantity || 0),
      unitPrice: Number(r.unitPrice || 0),
      totalCost: Number(r.totalCost || 0),
    })),
    coproductosPep: (explosionPep.resumenCoproductosPep?.rows || []).map((r) => ({
      product: r.product_name || '—',
      unit: r.unit_of_measure || '—',
      quantity: Number(r.quantity || 0),
      unitPrice: Number(r.unitPrice || 0),
      totalCost: Number(r.totalCost || 0),
      source: r.source_pep_name || '',
    })),
    subproductosRecuperados: (resumenByproducts.rows || []).map((r) => ({
      product: r.product || '—',
      unit: r.unit || '—',
      quantity: Number(r.quantity || 0),
    })),
    totales: {
      costoInsumosTotal,
      costoElaboradosTotal,
      tienePep: explosionPep.hasPep,
    },
  };
}

export async function loadProduccionGuiaInforme(guiaId, options = {}) {
  const { produccionFabricarPepDesdePtEnabled = true, onProgress } = options;

  onProgress?.('Cargando guía de producción…');
  const guiaRes = await produccionGuiaService.get(guiaId);
  if (!guiaRes?.success || !guiaRes.data) {
    throw new Error(guiaRes?.message || 'Guía no encontrada');
  }
  const guia = guiaRes.data;
  const itemsWithQty = getGuiaItemsWithQuantity(guia.rows || []);
  const esProductoTerminado = guia.guide_type === 'producto_terminado';
  const horaCorte = normalizarHoraHHmm(guia.guide_time, '08:00');

  const uniqueRecetaIds = Array.from(
    new Set(itemsWithQty.map((i) => (i.receta_plato_id != null ? String(i.receta_plato_id) : null)).filter(Boolean)),
  );

  onProgress?.(`Cargando ${uniqueRecetaIds.length} receta(s)…`);
  const recetasFullById = new Map();
  await Promise.all(
    uniqueRecetaIds.map(async (idStr) => {
      try {
        const res = await recetaPlatoService.get(idStr);
        if (res?.success && res.data) recetasFullById.set(idStr, res.data);
      } catch {
        /* sin ficha */
      }
    }),
  );

  onProgress?.('Cargando catálogo de productos…');
  const [productsRes, recetasRes] = await Promise.all([
    productService.getProducts({ active: true }),
    recetaPlatoService.list({ active_only: true }).catch(() => null),
  ]);

  const productsRaw = Array.isArray(productsRes?.data)
    ? productsRes.data
    : Array.isArray(productsRes?.data?.data)
      ? productsRes.data.data
      : [];

  const productUnitById = {};
  const productTipoById = {};
  const pepRecetaIdByProductId = {};

  productsRaw.forEach((p) => {
    if (p?.id == null) return;
    const id = String(p.id);
    const u = String(p?.unit_of_measure ?? '').trim();
    if (u) productUnitById[id] = u;
    productTipoById[id] = {
      linked_to_recipe: Boolean(p.linked_to_recipe),
      output_product_type: null,
      is_insumo: Boolean(p.is_insumo),
    };
  });

  const recetasRaw = Array.isArray(recetasRes?.data)
    ? recetasRes.data
    : Array.isArray(recetasRes?.data?.data)
      ? recetasRes.data.data
      : [];

  recetasRaw.forEach((r) => {
    const register = (productId, outputType, recetaId) => {
      if (productId == null) return;
      const id = String(productId);
      const prev = productTipoById[id] || { linked_to_recipe: true, output_product_type: null, is_insumo: false };
      productTipoById[id] = {
        ...prev,
        linked_to_recipe: true,
        output_product_type: outputType || prev.output_product_type || null,
      };
      if (outputType === 'producto_en_proceso' && recetaId != null) {
        pepRecetaIdByProductId[id] = recetaId;
      }
    };
    register(r?.product_id, r.output_product_type, r.id);
    (r.outputs || []).forEach((out) => {
      register(out.product_id ?? out.finished_product?.id, out.output_product_type, r.id);
    });
  });

  let stockCostByProduct = {};
  if (guia.warehouse_id && guia.guide_date) {
    onProgress?.('Cargando costos kárdex…');
    try {
      const resp = await entryGuideService.getProductStockAndCostBulk(
        String(guia.warehouse_id),
        null,
        guia.guide_date,
        horaCorte,
        true,
      );
      if (resp?.success && resp?.data && typeof resp.data === 'object') {
        stockCostByProduct = resp.data;
      }
    } catch {
      stockCostByProduct = {};
    }
  }

  const subproductoIdsEnGuia = [];
  if (esProductoTerminado) {
    const resumenInsumos = buildResumenInsumosGuia(itemsWithQty, recetasFullById);
    const seen = new Set();
    for (const row of resumenInsumos.filas || []) {
      if (row?.product_id == null) continue;
      const pid = String(row.product_id);
      if (seen.has(pid)) continue;
      if (isSubproductoProduct(row.product_id, productTipoById) && pepRecetaIdByProductId[pid]) {
        seen.add(pid);
        subproductoIdsEnGuia.push(pid);
      }
    }
  }

  const pepRecetasByProductId = new Map();
  if (subproductoIdsEnGuia.length > 0) {
    onProgress?.(`Cargando ${subproductoIdsEnGuia.length} receta(s) PEP…`);
    await Promise.all(
      subproductoIdsEnGuia.map(async (pid) => {
        const recetaId = pepRecetaIdByProductId[pid];
        if (recetaId == null) return;
        try {
          const res = await recetaPlatoService.get(recetaId);
          if (res?.success && res.data) pepRecetasByProductId.set(pid, res.data);
        } catch {
          /* sin ficha PEP */
        }
      }),
    );
  }

  onProgress?.('Armando informe…');
  return buildProduccionGuiaInforme({
    guia,
    itemsWithQty,
    recetasFullById,
    stockCostByProduct,
    productUnitById,
    productTipoById,
    pepRecetaIdByProductId,
    pepRecetasByProductId,
    produccionFabricarPepDesdePtEnabled,
    horaCorte,
  });
}
